<template>
  <div class="card-body">
    <!-- Vehicle Details -->
    <div class="form-row align-items-baseline">
      <div class="col-12">
        <h4>{{ trans('Vehicle Details') }}</h4>
        <hr class="solid mt-0">
      </div>
    </div>
    <div class="form-row align-items-baseline">
      <!-- Production Year -->
      <div class="form-group required col-lg-4">
        <label class="font-weight-bold text-dark text-2">{{ trans('Production Year') }}</label>
        <v-select v-model="prodYear"
                  :class="{ error: hasError('production_year') }"
                  :placeholder="trans('Production Year')"
                  :disabled="!years || !years.length"
                  :options="years" />
        <label class="error" v-if="hasError('production_year')">{{ errors.production_year[0] }}</label>
      </div>

      <!-- Color -->
      <div class="form-group required col-lg-4">
        <label class="font-weight-bold text-dark text-2">{{ trans('Color') }}</label>
        <div class="row">
          <div class="col-8">
            <v-select v-model="color.color"
                      @change="$emit('update:color', color)"
                      :class="{ error: hasError('color.color') }"
                      :placeholder="trans('Color')"
                      :reduce="option => option.value"
                      :loading="!colors || !colors.length"
                      :disabled="!colors || !colors.length"
                      :options="colors"
            >
              <template v-slot:option="option">
                <div class="color-preview" :style="'background-color: ' + option.color"></div>
                {{ option.label }}
              </template>

              <template v-slot:selected-option="option">
                <div class="color-preview" :style="'background-color: ' + option.color"></div>
                {{ option.label }}
              </template>
            </v-select>
          </div>
          <div class="col-4 px-0 d-flex align-items-center">
            <div class="custom-control custom-checkbox">
              <input type="checkbox" class="custom-control-input" id="is_color_metallic"
                     @change="$emit('update:color', color)"
                     v-model="color.is_metallic"
                     :class="{ error: hasError('color') }">
              <label class="custom-control-label" for="is_color_metallic">{{ trans('Metallic') }}</label>
            </div>
          </div>
        </div>

        <label class="error" v-if="hasError('color.color')">{{ errors['color.color'][0] }}</label>
      </div>

      <!-- VIN -->
      <div class="form-group col-lg-4">
        <label for="vin" class="font-weight-bold text-dark text-2">{{ trans('VIN') }}</label>
        <input id="vin" type="text" :placeholder="trans('VIN')" v-model="vinValue" maxlength="17"
               class="form-control" :class="{ error: hasError('vin') }"
               @change="$emit('update:vin', vinValue)" @input="$emit('update:vin', vinValue)">

        <label class="error" v-if="hasError('vin')">{{ errors.vin[0] }}</label>
      </div>
    </div>

    <!-- Registration Details -->
    <div class="form-row mt-5">
      <div class="col-12">
        <h4>{{ trans('Registration Details') }}</h4>
        <hr class="solid mt-0">
      </div>
    </div>
    <div class="form-row align-items-baseline">

      <!-- First Registration -->
      <div class="form-group required col-lg-4">
        <label class="font-weight-bold text-dark text-2">{{ trans('First Registration') }}</label>
        <div class="row">
          <div class="col-6 pr-0">
            <v-select v-model="regDate.month"
                      @input="$emit('update:reg_date', regDate)"
                      :class="{ error: hasError('reg_date.month') }"
                      :placeholder="trans('Month')"
                      :reduce="option => option.value"
                      :disabled="!months || !months.length"
                      :options="months" />
          </div>
          <div class="col-6">
            <v-select v-model="regDate.year"
                      @input="$emit('update:reg_date', regDate)"
                      :class="{ error: hasError('reg_date.year') }"
                      :placeholder="trans('Year')"
                      :disabled="!regYears || !regYears.length"
                      :options="regYears" />
          </div>

          <label class="error" v-if="hasError('reg_date.month')">{{ errors['reg_date.month'][0] }}</label>
          <label class="error" v-if="hasError('reg_date.year') && !hasError('reg_date.month')">{{ errors['reg_date.year'][0] }}</label>
        </div>
      </div>

      <!-- Registration Plate -->
      <div class="form-group required col-lg-4">
        <label for="reg_plate" class="font-weight-bold text-dark text-2">{{ trans('Registration Plate') }}</label>
        <input id="reg_plate" type="text"
               :placeholder="trans('Registration Plate')"
               v-model="regPlate"
               class="form-control" :class="{ error: hasError('reg_plate') }">
        <label class="error" v-if="hasError('reg_plate')">{{ errors['reg_plate'][0] }}</label>
      </div>
    </div>

    <!-- Registration Details -->
    <div class="form-row mt-5">
      <div class="col-12">
        <h4>{{ trans('Motorization') }}</h4>
        <hr class="solid mt-0">
      </div>
    </div>
    <div class="form-row align-items-baseline">
      <!-- Fuel Type  -->
      <div class="form-group required col-lg-4">
        <label class="font-weight-bold text-dark text-2">{{ trans('Fuel') }}</label>
        <v-select v-model="fuelType"
                  :class="{ error: hasError('fuel') }"
                  :placeholder="trans('Fuel')"
                  :reduce="options => options.value"
                  :loading="!fuelTypes || !fuelTypes.length"
                  :disabled="!fuelTypes || !fuelTypes.length"
                  :options="fuelTypes" />
        <label class="error" v-if="hasError('fuel')">{{ errors['fuel'][0] }}</label>
      </div>

      <!-- Power -->
      <power-input v-model="powerValue"
                   :unit="powerUnit"
                   @unitChange="value => this.powerUnit = value"
                   @hpValueChange="powerHpHandler" />

      <!-- Cylinder Capacity -->
      <capacity-input v-model="capacityValue"
                      :unit="capacityUnit"
                      @unitChange="value => this.capacityUnit = value"
                      @ccValueChange="capacityCcHandler"/>
    </div>
    <div class="form-row align-items-baseline">
      <!-- Gearbox  -->
      <div class="form-group required col-lg-4">
        <label class="font-weight-bold text-dark text-2">{{ trans('Transmission') }}</label>
        <v-select v-model="transmissionType"
                  :class="{ error: hasError('transmission') }"
                  :placeholder="trans('Transmission')"
                  :reduce="option => option.value"
                  :loading="!transmissions || !transmissions.length"
                  :disabled="!transmissions || !transmissions.length"
                  :options="transmissions" />
        <label class="error" v-if="hasError('transmission')">{{ errors['transmission'][0] }}</label>
      </div>

      <!-- Speedometer Display -->
      <div class="form-group col-lg-4">
        <label class="font-weight-bold text-dark text-2">{{ trans('Speedometer Display') }}</label>
        <v-select v-model="isSpeedometerInMiles"
                  :class="{ error: hasError('speedometer_mph') }"
                  :placeholder="trans('Speedometer Display')"
                  :clearable="false"
                  :reduce="option => option.value" :options="[
                    { value: true, label: 'Miles (mph)' },
                    { value: false, label: 'Kilometres (kph)' },
                ]" />
        <label class="error" v-if="hasError('speedometer_mph')">{{ errors['speedometer_mph'][0] }}</label>
      </div>

      <!-- Odometer -->
      <div class="form-group col-lg-4">
        <label for="odometer" class="font-weight-bold text-dark text-2">{{ trans('Odometer Value') }}</label>
        <input id="odometer"
               type="number"
               placeholder="123123"
               v-model="odometerValue"
               class="form-control no-arrows" :class="{ error: hasError('odometer') }">
        <label class="error" v-if="hasError('odometer')">{{ errors['odometer'][0] }}</label>
      </div>
    </div>
  </div>
</template>

<script>
  import PowerInput from '../Register/VehicleFilter/PowerInput';
  import CapacityInput from '../Register/VehicleFilter/CapacityInput';
  import {apiFetchEnum} from '../../api';

  export default {
    name: "VehicleFilter",

    components: {
      CapacityInput,
      PowerInput,
    },

    props: {
      productionYear: {
        validator: prop => typeof prop === 'number' || prop === null,
        required: true,
      },
      prodFrom: {
        type: Number,
        required: true,
      },
      prodTo: {
        type: Number,
        required: true,
      },
      color: {
        type: Object,
        required: true,
      },
      vin: {
        validator: prop => typeof prop === 'string' || prop === null,
        required: true,
      },
      reg_date: {
        type: Object,
        required: true,
      },
      reg_plate: {
        validator: prop => typeof prop === 'string' || prop === null,
        required: true,
      },
      fuel: {
        validator: prop => typeof prop === 'number' || prop === null,
        required: true,
      },
      transmission: {
        validator: prop => typeof prop === 'number' || prop === null,
        required: true,
      },
      speedometer_mph: {
        validator: prop => typeof prop === 'boolean' || prop === null,
        required: true,
      },
      odometer: {
        validator: prop => typeof prop === 'number' || typeof prop === 'string' || prop === null,
        required: true,
      },
      errors: {
        validator: prop => typeof prop === 'object' || prop === null,
        required: true,
        default: {},
      },
      capacity: {
        validator: prop => typeof prop === 'number' || typeof prop === 'string' || prop === null,
        required: true,
      },
      power: {
        validator: prop => typeof prop === 'number' || typeof prop === 'string' || prop === null,
        required: true,
      },
    },

    data() {
      return {
        fuelTypes: [],
        colors: [],
        transmissions: [],
        allMonths: null,

        bodyType: null,
        prodYear: this.productionYear,
        regDate: this.reg_date,
        regPlate: this.reg_plate,
        fuelType: this.fuel,
        vinValue: this.vin,

        powerValue: this.power,
        powerUnit: 'hp',
        powerHp: this.power,
        capacityValue: this.capacity,
        capacityUnit: 'cc',
        capacityCc: this.capacity,
        transmissionType: this.transmission,
        isSpeedometerInMiles: this.speedometer_mph,
        odometerValue: this.odometer,
      }
    },

    computed: {
      months() {
        return this.allMonths || [
          {value: 1, label: 'January'},
          {value: 2, label: 'February'},
          {value: 3, label: 'March'},
          {value: 4, label: 'April'},
          {value: 5, label: 'May'},
          {value: 6, label: 'June'},
          {value: 7, label: 'July'},
          {value: 8, label: 'August'},
          {value: 9, label: 'September'},
          {value: 10, label: 'October'},
          {value: 11, label: 'November'},
          {value: 12, label: 'December'},
        ];
      },

      /**
       * Years that could be used to select in First Registration field
       */
      regYears() {
        const result = [];
        const startYear = this.prodFrom || 1900;
        const currentYear = (new Date()).getFullYear();

        for (let i = startYear; i <= currentYear; i++) {
          result.unshift(i);
        }

        return result;
      },

      years() {
        const years = [];
        const prodFrom = this.prodFrom;
        const prodTo = this.prodTo;

        for (let i = prodFrom; i <= prodTo; i++) {
          if (years.indexOf(i) === -1) {
            years.push(i);
          }
        }

        return years;
      },
    },

    mounted() {
      Promise
        .all([
          apiFetchEnum('color'),
          apiFetchEnum('fuel_type'),
          apiFetchEnum('transmission'),
          apiFetchEnum('month'),
        ])
        .then(values => {
          [
            this.colors,
            this.fuelTypes,
            this.transmissions,
            this.allMonths,
          ] = values;
        });
    },

    methods: {
      powerHpHandler(val) {
        this.$emit('update:power', val);
      },

      capacityCcHandler(val) {
        this.$emit('update:capacity', val);
      },

      hasError(fieldName) {
        return this.errors.hasOwnProperty(fieldName);
      }
    },

    watch: {
      prodYear(val) {
        this.$emit('update:productionYear', val)
      },

      regPlate(val) {
        this.$emit('update:reg_plate', val);
      },

      fuelType(val) {
        this.$emit('update:fuel', val);
      },

      powerHp(val) {
        this.$emit('update:power', val)
      },

      capacityCc(val) {
        this.$emit('update:capacity', val)
      },

      transmissionType(val) {
        this.$emit('update:transmission', val)
      },

      isSpeedometerInMiles(val) {
        this.$emit('update:speedometer_mph', val)
      },

      odometerValue(val) {
        this.$emit('update:odometer', Number(val))
      },
    },
  }
</script>
